import { Enum, PricingType, PricingTypes, ProjectType } from "../../../common/constants"
import { WorkareaGroup } from "../../../domain/workareas/group"
import { PaymentScheduleRow } from "../../../domain/proposals/settings/proposalSettings"

export type ProposalJobData = {
  id: number
  name: string
  type: ProjectType
  estimateDate: string
  customer?: ProposalCustomerData
  salesPersonName: string
  salesPersonEmail: string
}

type ProposalCustomerData = {
  isActive: boolean
  displayName: string
  emails: EmailView[]
}

export type EmailView = {
  address: string,
  isPrimary: boolean
}

type Recipient = {
  id: string
  email: string
  automationActive: boolean
  totalFollowUps: number
  followUpsSent: number
  nextFollowUpAt: string
}

export type Workarea = {
  id: number
  name: string
  quotedPrice: number
  description: string
  pricingType: PricingType
  locked: boolean,
  estimatedSalesTax: number
  masterWorkareaId: number
  isOnHold: boolean
  isApproved: boolean
  sortOrder: number
  workareaGroupId?: string
}

export const ProposalStatuses = {
  Draft: 'draft',
  InReview: 'in-review',
  Approved: 'approved',
  Archived: 'archived'
} as const

export type ProposalStatus = Enum<typeof ProposalStatuses>

export type Proposal = {
  id: number
  version?: number
  projectId: number
  initiateDateUtc: string
  initiatedByEmail: string
  initiatedBy: string
  isApproved: boolean
  approveDateUtc: string
  recipients: Recipient[]
  workareas: Workarea[]
  totalPrice: number
  termsAndConditions: string
  initialAttachmentId?: string
  approvedAttachmentId?: string
  internalName?: string
  versionNumber: number
  status: ProposalStatus
  lastEditedAtUtc?: string
}

export type ProposalSummary = {
  firstCreatedUtc?: string
  firstSentUtc?: string
  approvedUtc?: string
  totalVersions: number
  totalViews: number
  daysInReview?: number
  followUpsSent: number
}

export type ProposalEvent = {
  id: string
  proposalId: number
  customerUserId?: string
  email?: string
  eventType: string
  timestamp: string
}

export type CollapsedProposalEvent = {
  eventType: string
  email?: string
  count: number
  earliestTimestamp: string
  latestTimestamp: string
}

export const proposalViewedEventType = 'proposal-customer-portal-viewed'

export const collapseProposalEvents = (events: ProposalEvent[]): CollapsedProposalEvent[] => {
  const collapsed: CollapsedProposalEvent[] = []
  const sortedEvents = [...events].sort((a, b) => a.timestamp.localeCompare(b.timestamp))

  sortedEvents.forEach(event => {
    const previous = collapsed[collapsed.length - 1]
    const isConsecutiveViewBySameEmail =
      previous !== undefined &&
      event.eventType === proposalViewedEventType &&
      previous.eventType === proposalViewedEventType &&
      (event.email?.toLowerCase() ?? '') === (previous.email?.toLowerCase() ?? '')

    if (isConsecutiveViewBySameEmail) {
      previous.count += 1
      if (event.timestamp < previous.earliestTimestamp) {
        previous.earliestTimestamp = event.timestamp
      }
      if (event.timestamp > previous.latestTimestamp) {
        previous.latestTimestamp = event.timestamp
      }
      return
    }

    collapsed.push({
      eventType: event.eventType,
      email: event.email,
      count: 1,
      earliestTimestamp: event.timestamp,
      latestTimestamp: event.timestamp,
    })
  })

  return collapsed
}

export const defaultProposal: Proposal = {
  id: 0,
  projectId: 0,
  initiateDateUtc: '',
  initiatedByEmail: '',
  initiatedBy: '',
  isApproved: false,
  approveDateUtc: '',
  recipients: [],
  workareas: [],
  totalPrice: 0,
  termsAndConditions: '',
  versionNumber: 0,
  status: ProposalStatuses.Draft
}
export const defaultRecipient = { id: '', email: '' }
export const defaultWorkarea: Workarea = {
  id: 0,
  name: '',
  quotedPrice: 0,
  pricingType: PricingTypes.FlatRate,
  description: '',
  locked: false,
  estimatedSalesTax: 0,
  masterWorkareaId: 0,
  isOnHold: false,
  isApproved: false,
  sortOrder: 0,
}

export type DisableFollowUp = {
  proposalId: number
  customerUserId: string
}

export const approvedProposalAttachment = 'approved-proposal'
export const initialProposalAttachment = 'initial-proposal'

export const calculateProposalAmount = (proposal: Proposal | undefined): number => {
  if (proposal === undefined) {
    return 0
  }
  return proposal.workareas.map(x => x.quotedPrice).reduce((acc, v) => acc + v, 0)
}

export const workareasToDisplay = (workareas: Workarea[]): Workarea[] => {
  if (workareas.length < 5) {
    return workareas
  }

  const displayWorkareas: Workarea[] = []
  new Set(workareas.map(x => x.name))
    .forEach(workareaName => {
      const workarea = workareas.filter(x => x.name === workareaName)[0]
      displayWorkareas.push(
        {
          id: workarea.id,
          name: workareaName,
          quotedPrice: workareas.filter(x => x.name === workareaName).reduce((total, workarea) => { return total + workarea.quotedPrice }, 0),
          pricingType: workarea.pricingType,
          description: workarea.description,
          locked: workarea.locked,
          estimatedSalesTax: workarea.estimatedSalesTax,
          masterWorkareaId: workarea.masterWorkareaId,
          isOnHold: workarea.isOnHold,
          isApproved: workarea.isApproved,
          sortOrder: workarea.sortOrder
        }
      )
    })
  return displayWorkareas
}

export const getQuotedPriceForWorkareas = (workareas: Workarea[]) => {
  return workareas.reduce((sum, workarea) => sum + workarea.quotedPrice, 0)
}

export const calculateSalesTax = (workareas: Workarea[]) => {
  return workareas.reduce((total, x) => total + (x.estimatedSalesTax || 0), 0)
}

export const calculatePerInvoiceAmounts = (workareas: Workarea[], invoiceCount: number) => {
  const amountWithoutSalesTax = calculateTotalPrice(workareas)
  const salesTax = calculateSalesTax(workareas)
  const totalAmount = amountWithoutSalesTax + salesTax
  const perInvoiceAmount = Math.round(totalAmount / invoiceCount)
  const perInvoiceWithoutSalesTax = Math.round(amountWithoutSalesTax / invoiceCount)
  const salesTaxPerInvoice = salesTax > 0 ? perInvoiceAmount - perInvoiceWithoutSalesTax : 0

  return { perInvoiceAmount, salesTaxPerInvoice }
}

export const calculateTotalPrice = (workareas: any[]) => {
  return workareas.reduce((total, workarea) => total + workarea.quotedPrice, 0)
}

export const hasActiveAutomations = (latestProposal: Proposal): boolean => {
  return latestProposal.recipients.some(x=>x.automationActive)
}

export const updatePaymentScheduleAmounts = (paymentSchedule: PaymentScheduleRow[], workareas: Workarea[], totalPrice: number): PaymentScheduleRow[] => {
  let remaining = totalPrice
  return paymentSchedule.map((x, i, array) => {
    const amount = i === array.length - 1
      ? remaining
      : workareas.reduce((total, workarea) => total + Math.round(workarea.quotedPrice * x.percentage), 0)
    remaining -= amount
    return { ...x, amount }
  })
}

export type ProposalWorkareaGroup = {
  id: string
  name: string
  locked: boolean
  workareaCount: number
  isOnHold: boolean
  quotedPrice: number
  description: string
  sortIndex: number
  pricingType: PricingType
}

export const proposalStatusLabels: Record<ProposalStatus, string> = {
  [ProposalStatuses.Draft]: 'Draft',
  [ProposalStatuses.InReview]: 'In Review',
  [ProposalStatuses.Approved]: 'Approved',
  [ProposalStatuses.Archived]: 'Archived'
}

export const proposalStatusColors: Record<ProposalStatus, string> = {
  [ProposalStatuses.Draft]: 'blue',
  [ProposalStatuses.InReview]: 'gold',
  [ProposalStatuses.Approved]: 'green',
  [ProposalStatuses.Archived]: 'red'
}

export const sortProposalsByVersion = (proposals: Proposal[]): Proposal[] => {
  return [...proposals].sort((a, b) => {
    if (b.versionNumber !== a.versionNumber) {
      return b.versionNumber - a.versionNumber
    }
    return b.id - a.id
  })
}

export const findCurrentProposal = (proposals: Proposal[]): Proposal | undefined => {
  const sorted = sortProposalsByVersion(proposals)
  return sorted.find(x => x.status !== ProposalStatuses.Archived) ?? sorted[0]
}

export const proposalDisplayName = (proposal: Proposal): string => {
  const name = proposal.internalName?.trim()
  return name !== undefined && name.length > 0 ? name : 'Untitled Proposal'
}

export const countProposalGroups = (proposal: Proposal): number => {
  const groupIds = proposal.workareas
    .map(x => x.workareaGroupId)
    .filter((x): x is string => x !== undefined && x !== null)
  return new Set(groupIds).size
}

export const formatDaysInReview = (daysInReview: number | undefined): string | undefined => {
  if (daysInReview === undefined || daysInReview === null) {
    return undefined
  }
  if (daysInReview === 0) {
    return 'Less than a day'
  }
  return daysInReview === 1 ? '1 Day' : `${daysInReview} Days`
}

export const countFollowUpsSent = (proposal: Proposal): number => {
  return proposal.recipients.reduce((total, x) => total + (x.followUpsSent ?? 0), 0)
}

/**
 * Which stored file is this proposal's PDF: an approved proposal has its own
 * frozen copy, everything else falls back to the one made when it was created.
 */
export const currentProposalAttachmentId = (proposal: Proposal): string | undefined =>
  proposal.status === ProposalStatuses.Approved && proposal.approvedAttachmentId
    ? proposal.approvedAttachmentId
    : proposal.initialAttachmentId

export const groupProposalWorkareas = (groups: WorkareaGroup[], workareas: Workarea[]): ProposalWorkareaGroup[] => {
  const result: ProposalWorkareaGroup[] = []
  groups.forEach(group => {
    const groupWorkareas = workareas.filter(x => x.workareaGroupId === group.id)
    if (groupWorkareas.length === 0) {
      return
    }
    const workarea = groupWorkareas[0]
    
    result.push({
      id: group.id,
      name: group.name,
      locked: groupWorkareas.some(x => x.locked),
      workareaCount: groupWorkareas.length,
      isOnHold: group.isOnHold,
      quotedPrice: groupWorkareas.reduce((total, workarea) => total + workarea.quotedPrice, 0),
      description: workarea.description,
      sortIndex: group.sortIndex,
      pricingType: workarea.pricingType
    })
  })
  result.sort((a, b) => a.sortIndex - b.sortIndex)
  return result
}
